import { useState } from "react";
import JSZip from "jszip";
import { Button } from "../../client/components/ui/button";
import { Download, FileArchive, ImageDown, Instagram } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import type { ReportFormat } from "../metrics/types";

type Props = { signedUrls: string[]; shareToken: string; format: ReportFormat };

function isMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * Fetch every signed URL and wrap the bytes in `File` objects so they can be
 * handed to the Web Share API. The S3 bucket has CORS configured.
 */
async function fetchAsFiles(signedUrls: string[], shareToken: string): Promise<File[]> {
  const files: File[] = [];
  for (let i = 0; i < signedUrls.length; i++) {
    const res = await fetch(signedUrls[i], { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const blob = await res.blob();
    files.push(
      new File([blob], `edgely-report-${shareToken}-${i + 1}.png`, {
        type: blob.type || "image/png",
      }),
    );
  }
  return files;
}

/**
 * Save images to the device's camera roll on mobile.
 *
 * A downloaded file lands in "Files", not Photos — useless for posting to
 * Instagram/TikTok. The Web Share API's native sheet is the only reliable
 * web path to the camera roll: it offers "Save N Images" / "Save to Photos".
 *
 * Tries to share all files at once; if the platform rejects a multi-file
 * payload, falls back to sharing them one by one. Returns false when the
 * Web Share API (with files) isn't available at all — caller should then
 * fall back to a plain download.
 */
async function saveToCameraRoll(files: File[]): Promise<boolean> {
  const nav = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };
  if (!nav.share || !nav.canShare) return false;

  if (nav.canShare({ files })) {
    try {
      await nav.share({ files });
      return true;
    } catch (err) {
      // AbortError = user dismissed the sheet; treat as handled, don't fall back.
      if (err instanceof DOMException && err.name === "AbortError") return true;
      throw err;
    }
  }

  // Multi-file not supported → share each image individually.
  let sharedAny = false;
  for (const file of files) {
    if (!nav.canShare({ files: [file] })) continue;
    try {
      await nav.share({ files: [file] });
      sharedAny = true;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return true;
      throw err;
    }
  }
  return sharedAny;
}

function TiktokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

/**
 * Force a real download (vs the browser opening the image in a new tab).
 *
 * The HTML `download` attribute is ignored for cross-origin URLs by Chrome
 * and others — the image just opens. To get a true "Save as" we have to
 * fetch the bytes (works because the S3 bucket has CORS configured), wrap
 * them in a blob: URL (same-origin), and click an anchor pointing at that.
 */
async function downloadOne(url: string, filename: string) {
  const res = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Release the object URL once the browser has consumed it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

async function triggerDownloads(signedUrls: string[], shareToken: string) {
  for (let i = 0; i < signedUrls.length; i++) {
    try {
      await downloadOne(signedUrls[i], `edgely-report-${shareToken}-${i + 1}.png`);
      // Give the browser a beat to commit the download before doing anything
      // else (next file, or — in openApp — navigating away to a social site).
      await new Promise((r) => setTimeout(r, 250));
    } catch (err) {
      // Fallback: open the URL in a new tab so the user can still save
      // manually (right-click → Save as). Better than silent failure.
      // eslint-disable-next-line no-console
      console.error("Direct download failed, opening tab", err);
      window.open(signedUrls[i], "_blank", "noopener,noreferrer");
    }
  }
}

/**
 * Bundle every slide into a single ZIP so the user gets one file instead of
 * one "Save as" dialog per image. Fetches the S3 bytes (CORS-enabled bucket),
 * zips them client-side, and triggers a single blob: download.
 */
async function downloadAllAsZip(signedUrls: string[], shareToken: string) {
  const zip = new JSZip();
  for (let i = 0; i < signedUrls.length; i++) {
    const res = await fetch(signedUrls[i], { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    zip.file(`edgely-report-${shareToken}-${i + 1}.png`, await res.blob());
  }
  const archive = await zip.generateAsync({ type: "blob" });
  const objectUrl = URL.createObjectURL(archive);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = `edgely-report-${shareToken}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Save a report's images the best way for the current device:
 * - Mobile → Web Share sheet, so the images land in the camera roll.
 * - Desktop → a single ZIP (multi-slide) or a direct download (single image).
 * Falls back to sequential per-file downloads if anything fails.
 */
export async function saveReportImages(signedUrls: string[], shareToken: string) {
  if (signedUrls.length === 0) return;
  try {
    if (isMobileUserAgent()) {
      const files = await fetchAsFiles(signedUrls, shareToken);
      const shared = await saveToCameraRoll(files);
      if (shared) return;
      // Web Share unavailable → fall through to download.
    }
    if (signedUrls.length > 1) {
      await downloadAllAsZip(signedUrls, shareToken);
    } else {
      await downloadOne(signedUrls[0], `edgely-report-${shareToken}-1.png`);
    }
  } catch (err) {
    // Last-resort fallback: sequential per-file downloads.
    // eslint-disable-next-line no-console
    console.error("Save failed, falling back to sequential downloads", err);
    await triggerDownloads(signedUrls, shareToken);
  }
}

export function ReportShareActions({ signedUrls, shareToken, format }: Props) {
  const t = useTranslation();
  const [hint, setHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMultiSlide = signedUrls.length > 1;
  const mobile = isMobileUserAgent();

  async function handleSave() {
    setBusy(true);
    try {
      await saveReportImages(signedUrls, shareToken);
    } finally {
      setBusy(false);
    }
  }

  const saveLabel = busy
    ? mobile
      ? t.reports.saving
      : t.reports.generating
    : mobile
      ? t.reports.saveToPhotos
      : isMultiSlide
        ? t.reports.downloadAll
        : t.reports.download;
  const SaveIcon = mobile ? ImageDown : isMultiSlide ? FileArchive : Download;

  const igDeepLink = format === "story" ? "instagram://story-camera" : "instagram://library";
  const igWebFallback = "https://www.instagram.com/";
  const tiktokUrl = "https://www.tiktok.com/upload";
  const xDeepLink = "twitter://post";
  const xWebFallback = "https://x.com/compose/post";

  async function openApp(opts: {
    deepLink?: string;
    webFallback: string;
    requireMobile?: boolean;
  }) {
    // Get the images onto the device first: camera roll on mobile (so they're
    // pickable in the social app), plain download on desktop.
    if (mobile) {
      try {
        const files = await fetchAsFiles(signedUrls, shareToken);
        const shared = await saveToCameraRoll(files);
        if (!shared) await triggerDownloads(signedUrls, shareToken);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Camera-roll save failed, downloading instead", err);
        await triggerDownloads(signedUrls, shareToken);
      }
    } else {
      await triggerDownloads(signedUrls, shareToken);
      // Chrome silently drops a programmatic download if the tab loses focus
      // before the browser has committed it. Opening the social site below
      // steals focus, so give the download a brief head start to land first.
      await new Promise((r) => setTimeout(r, 600));
    }
    if (opts.requireMobile && !mobile) {
      setHint(true);
      window.open(opts.webFallback, "_blank", "noopener,noreferrer");
      return;
    }
    if (mobile && opts.deepLink) {
      const opened = Date.now();
      window.location.href = opts.deepLink;
      window.setTimeout(() => {
        if (Date.now() - opened < 2500 && document.visibilityState === "visible") {
          window.open(opts.webFallback, "_blank", "noopener,noreferrer");
        }
      }, 1500);
      return;
    }
    window.open(opts.webFallback, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={handleSave} disabled={busy}>
          <SaveIcon className="h-4 w-4 mr-2" />
          {saveLabel}
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() =>
            openApp({ deepLink: igDeepLink, webFallback: igWebFallback, requireMobile: true })
          }
        >
          <Instagram className="h-4 w-4 mr-2" />
          {t.reports.openInstagram}
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() => openApp({ webFallback: tiktokUrl })}
        >
          <TiktokIcon className="h-4 w-4 mr-2" />
          {t.reports.openTiktok}
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() => openApp({ deepLink: xDeepLink, webFallback: xWebFallback })}
        >
          <XIcon className="h-4 w-4 mr-2" />
          {t.reports.openX}
        </Button>
      </div>
      {hint && (
        <p className="text-xs text-muted-foreground">{t.reports.socialDesktopHint}</p>
      )}
    </div>
  );
}
